import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Link } from "react-router-dom";
import { Alert, Card, CardBody, Spinner } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import TableContainer from "../../components/Common/TableContainer";
import { formatDate } from "../../helpers/helper_funcations";
import { deleteCompany, getCompanies, clearCompanyErrors, getDocuments } from "store/actions";
import ConfirmModal from "components/Common/ConfirmModal";

const CompaniesTable = (props) => {
  const dispatch = useDispatch();
  const hasInteracted = useRef(false);

  const {
    message,
    loading,
    deleteCompanyLoading,
    confirmDeleted,
    companiesData,
    pagination
  } = useSelector(state => state.Company)
  const { allDocuments } = useSelector((state) => state.Documents || {})

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState("")
  const [docName, setDocName] = useState("")
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null })
  const [pendingCompany, setPendingCompany] = useState(null)

  const openConfirm = (company) => {
    setPendingDeleteId(company?.id);
    setPendingCompany({ name: company?.name, domain: company?.domain });
    setConfirmOpen(true);
  }

  const handleConfirmDelete = () => {
    if (!pendingDeleteId) return
    dispatch(deleteCompany(props.router.navigate, getCompanies, pendingDeleteId))
  }

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setPendingDeleteId(null);
    setPendingCompany(null);
  }

  const onSearch = (value) => {
    hasInteracted.current = true;
    setPageIndex(0)
    setSearch(value)
  }

  const onPageChange = (nextPageIndex) => {
    hasInteracted.current = true;
    setPageIndex(nextPageIndex)
  }

  const onPageSizeChange = (nextSize) => {
    hasInteracted.current = true;
    setPageIndex(0)
    setPageSize(nextSize)
  }

  const onDocChange = (value) => {
    hasInteracted.current = true;
    setPageIndex(0)
    setDocName(value || "")
  }

  const onDateRangeChange = (dates = []) => {
    hasInteracted.current = true;
    setPageIndex(0);
    const [start, end] = dates || [];
    const startISO = start ? new Date(start).toISOString() : null;
    const endISO = end
      ? new Date(new Date(end).setHours(23, 59, 59, 999)).toISOString()
      : null;
    setDateRange({ startDate: startISO, endDate: endISO });
  };

  // const handleClearFilters = () => {
  //   setSearch("");
  //   setDocName("");
  //   setDateRange({ startDate: null, endDate: null });
  //   setPageIndex(0);
  // };

  const documentsList = useMemo(
    () => (Array.isArray(allDocuments) ? allDocuments : []),
    [allDocuments]
  );

  useEffect(() => {
    if (!hasInteracted.current) return;
    dispatch(getCompanies({
      page: pageIndex + 1,
      limit: pageSize,
      search,
      docName,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }));
  }, [pageIndex, pageSize, search, docName, dateRange.startDate, dateRange.endDate, dispatch]);

  useEffect(() => {
    if (!deleteCompanyLoading && confirmDeleted && confirmOpen) {
      setConfirmOpen(false);
      setPendingDeleteId(null);
      setPendingCompany(null);
    }
  }, [deleteCompanyLoading, confirmDeleted])

  useEffect(() => {
    const t = setTimeout(() => dispatch(clearCompanyErrors()), 8000)
    return () => clearTimeout(t)
  }, [message])

  const columns = useMemo(
    () => [
      {
        Header: "Company",
        accessor: "name",
        disableFilters: true,
        Cell: ({ row }) => {
          const { name, logoUrl, domain } = row.original;
          const src = logoUrl || `https://logo.clearbit.com/${domain}`;
          return (
            <div className="d-flex align-items-center">
              <img
                src={src}
                alt={name}
                onError={(e) => {
                  e.currentTarget.style.visibility = "hidden";
                }}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  objectFit: "cover",
                  marginRight: 8,
                  border: "1px solid #e5e7eb",
                }}
              />
              <span>{name}</span>
            </div>
          );
        },
      },
      {
        Header: "Domain",
        accessor: "domain",
        disableFilters: true,
        Cell: ({ value }) => (
          <a
            href={`https://${value}`}
            target="_blank"
            rel="noreferrer"
            className="text-decoration-underline"
          >
            {value}
          </a>
        ),
      },
      {
        Header: "Resources",
        accessor: row => (Array.isArray(row?.documents) ? row.documents.length : 0),
        id: "documentsCount",
        disableFilters: true,
        filterable: false,
        sortType: "basic",
        Cell: cellProps => {
          const user = cellProps.row.original;
          const docs = Array.isArray(user?.documents) ? user.documents : [];
          const count = docs.length;

          if (!count) return <span style={{ opacity: 0.7 }}>—</span>;

          const preview = docs.slice(0, 3);
          return (
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <span className="badge bg-light text-dark">{count}</span>
              {preview.map(d => (
                <span key={d.id} className="badge bg-secondary">
                  {d.name || d.fileName || d.id}
                </span>
              ))}
              {count > 3 && (
                <span className="badge bg-light text-dark">+{count - 3} more</span>
              )}
            </div>
          );
        },
      },
      {
        Header: "Primary Color",
        accessor: "primaryColor",
        disableFilters: true,
        Cell: ({ value }) => (
          <div className="d-flex align-items-center gap-2">
            <span
              title={value}
              style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                display: "inline-block",
                background: value,
                border: "1px solid #e5e7eb",
              }}
            />
            <code className="text-muted">{value}</code>
          </div>
        ),
      },
      {
        Header: "Created",
        accessor: "created_at",
        disableFilters: true,
        Cell: ({ value }) => <span>{formatDate(value)}</span>,
      },
      {
        Header: "Action",
        disableFilters: true,
        Cell: ({ row }) => {
          const id = row.original.id;
          return (
            <div className="flex-row">
              {/* <Link className="download-rounded-btn" to={`/company/${id}`}>
                <i
                  className="mdi mdi-eye"
                  style={{ color: "white", fontSize: 15 }}
                ></i>
              </Link> */}
              <Link className="download-rounded-btn" to={`/company/${id}`}>
                <i
                  className="mdi mdi-pencil"
                  style={{ color: "white", fontSize: 15 }}
                ></i>
              </Link>
              <button
                type="button"
                className="download-rounded-btn"
                onClick={() => openConfirm(row.original)}
                disabled={deleteCompanyLoading}
                title={deleteCompanyLoading ? "Deleting…" : "Delete"}
                style={{ border: "none" }}
              >
                <i className="mdi mdi-delete" style={{ color: "white", fontSize: 15 }} />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <div className="row-spaceBtwn align-items-center mb-2">
        <div className="h2 mb-0 heading">Companies</div>
      </div>
      {/* <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => handleClearFilters()}
        >
          Clear Filters
        </button>
      </div> */}

      <Card className="shadow-none mb-0">
        <CardBody style={{ background: "#F8F8FB" }} className="pb-0 px-0 px-sm-4">
          {!!message && <Alert color="danger" className="mb-4">
            {String(message)}
          </Alert>}

          <TableContainer
            columns={columns}
            data={companiesData}
            isGlobalFilter={true}
            isJobListGlobalFilter={true}
            isAddOptions={false}
            isPagination={true}
            iscustomPageSizeOptions={false}
            customPageSize={10}
            totalPages={pagination.totalPages}
            manual
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSearch={onSearch}
            searchValue={search}
            onFilterChange={onDocChange}
            filterValue={docName}
            filterData={documentsList}
            loading={loading}
            onDateRangeChange={onDateRangeChange}
            dateRangeValue={dateRange}
            pagination="pagination pagination-rounded justify-content-end mb-2"
          />
        </CardBody>
        <ConfirmModal
          isOpen={confirmOpen}
          title="Confirm Delete"
          confirmLabel={deleteCompanyLoading ? "Deleting…" : "Delete"}
          confirmColor="danger"
          loading={deleteCompanyLoading}
          errorMessage={message?.message || ""}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        >
          <p className="mb-2">
            Are you sure you want to permanently delete{" "}
            <strong>{pendingCompany?.name || "this company"}</strong>?
          </p>

          <p className="mb-2">
            This will also <strong>delete all users</strong> whose email addresses end with{" "}
            <code>@{pendingCompany?.domain}</code>.
          </p>

          <p className="text-danger mb-0">
            This action cannot be undone.
          </p>
        </ConfirmModal>
      </Card>
    </React.Fragment>
  );
};

CompaniesTable.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(CompaniesTable);
